import { AdminOnlyBadge } from '@/components/admin-only-badge';
import {
  formatNavCount,
  useNeedsReplyCount,
} from '@/features/conversations/components/inbox-nav-badge';
import { MobileQuickAddSheet } from '@/features/mobile-quick-add';
import { stripBranchFromPath } from '@/features/organization-locations/branch-path';
import { useActiveOrganization } from '@/features/organization/api/get-active-organization/get-active-organization.hook';
import {
  canAccessBorradhBookingSurfaces,
  isAdminOnlyBorradhNavigation,
} from '@/lib/dashboard-nav';
import { BRANCH_PATHS, ROUTES } from '@/lib/route-paths';
import { useSession } from '@/lib/session';
import { useResolvedRoutes } from '@/lib/use-routes';
import { Link, useRouterState } from '@tanstack/react-router';
import type { LucideIcon } from 'lucide-react';
import {
  Calendar,
  LayoutGrid,
  MessageCircle,
  Plus,
  Sparkles,
} from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/lib/utils';

import {
  MOBILE_TAB_BAR_ROW_HEIGHT_PX,
  mobileTabBarBottomOffsetStyle,
} from './mobile-bottom-tabs-layout';

const INACTIVE = '#757575';
// The brand colour, not black: black-vs-grey read as "barely different" on a
// small icon, so the selected tab was hard to spot at a glance.
const ACTIVE = 'var(--primary)';
const ROW_HEIGHT = MOBILE_TAB_BAR_ROW_HEIGHT_PX;

function tabColor(active: boolean): string {
  return active ? ACTIVE : INACTIVE;
}

function isAiTabActive(pathname: string): boolean {
  if (pathname === ROUTES.dashboard || pathname === `${ROUTES.dashboard}/`) {
    return true;
  }
  return pathname.startsWith(BRANCH_PATHS.home);
}

function isBookingsTabActive(pathname: string): boolean {
  return pathname.startsWith(BRANCH_PATHS.calendar);
}

/**
 * Mobile bottom navbar: a solid bar docked to the bottom edge. Borradh-booking
 * organizations get four tabs and a centred quick-add button; a real user of
 * an external booking system gets the three non-booking destinations.
 *
 * Inbox is a TAB. It used to be a header shortcut beside the bell and the
 * avatar, which put the one surface a user opens all day behind a 48px glass
 * circle while Socials — a place you visit to plan, not to answer someone —
 * held a labelled tab. The header trio is gone (Profile and Notifications moved
 * into More), so the thing people actually come back for gets the tab, and
 * Socials keeps its place under Marketing in More.
 */
export function MobileBottomTabs() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const routes = useResolvedRoutes();
  const { data: organization } = useActiveOrganization();
  const { data: session } = useSession();
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const isImpersonating = Boolean(session?.session?.impersonatedBy);
  const showBorradhBookingControls = canAccessBorradhBookingSurfaces(
    organization?.bookingDestination,
    isImpersonating
  );
  const showAdminOnlyBadges = isAdminOnlyBorradhNavigation(
    organization?.bookingDestination,
    isImpersonating
  );

  // Real URLs are branch-scoped (`/dashboard/l/<branch>/home`) but the tab
  // matchers compare against the unscoped paths, so strip the branch first —
  // without it Claire and Bookings never lit up on a branch URL.
  const activePath = stripBranchFromPath(pathname);
  const needsReplyCount = useNeedsReplyCount();
  const aiActive = isAiTabActive(activePath);
  const bookingsActive = isBookingsTabActive(activePath);
  const inboxActive = pathname.startsWith(routes.conversations);
  const moreActive = activePath.startsWith(ROUTES.more);

  return (
    <>
      <nav
        aria-label="Main"
        data-mobile-bottom-tabs=""
        className="fixed inset-x-0 bottom-0 z-[49] border-t border-[#E5E5E5] bg-white md:hidden"
        style={{
          ...mobileTabBarBottomOffsetStyle(),
          // Own view-transition group: keeps the bar anchored while the route
          // view transition fades/slides the screen content underneath it.
          viewTransitionName: 'mobile-bottom-tabs',
        }}
      >
        <div
          className={cn(
            'grid items-center',
            showBorradhBookingControls ? 'grid-cols-5' : 'grid-cols-3'
          )}
          style={{ height: ROW_HEIGHT }}
        >
          {/* Bookings sits far left, Claire beside it, the quick-add button in
              the middle, then Inbox and More. Without Borrah's own booking
              surfaces there is no Bookings tab and no button. */}
          {showBorradhBookingControls ? (
            <TabLink
              active={bookingsActive}
              adminOnly={showAdminOnlyBadges}
              icon={Calendar}
              label="Bookings"
              to={routes.calendarDay}
            />
          ) : null}
          <TabLink
            to={routes.home}
            label="Claire"
            icon={Sparkles}
            active={aiActive}
          />
          {showBorradhBookingControls ? (
            <>
              <div className="relative flex h-full items-center justify-center">
                <button
                  type="button"
                  id="mobile-tab-quick-add"
                  data-testid="mobile-tab-quick-add"
                  // NOT "Add new". Pages carry their own "Add new" button (a new
                  // sale, a new client, …), so on mobile that name resolved to TWO
                  // different controls on the same screen: this FAB and the page's.
                  // A screen-reader user hears "Add new" twice for two different
                  // behaviours, and any by-name lookup is ambiguous. This one opens
                  // the cross-cutting quick-add menu, so it gets its own name.
                  aria-label="Quick add"
                  aria-haspopup="dialog"
                  aria-expanded={quickAddOpen}
                  onClick={() => setQuickAddOpen(true)}
                  className="bg-primary text-primary-foreground flex size-14 items-center justify-center rounded-full transition active:scale-[0.97]"
                >
                  <Plus className="size-7" strokeWidth={2.25} aria-hidden />
                </button>
                {showAdminOnlyBadges ? (
                  <AdminOnlyBadge className="absolute top-0 left-1/2 translate-x-3" />
                ) : null}
              </div>
            </>
          ) : null}
          <TabLink
            to={routes.conversations}
            label="Inbox"
            icon={MessageCircle}
            active={inboxActive}
            count={needsReplyCount}
          />
          <TabLink
            to={ROUTES.more}
            label="More"
            icon={LayoutGrid}
            active={moreActive}
          />
        </div>
      </nav>

      {showBorradhBookingControls ? (
        <MobileQuickAddSheet
          open={quickAddOpen}
          onOpenChange={setQuickAddOpen}
        />
      ) : null}
    </>
  );
}

function TabLink({
  to,
  label,
  icon: Icon,
  active,
  adminOnly = false,
  count = 0,
}: {
  to: string;
  label: string;
  icon: LucideIcon;
  active: boolean;
  adminOnly?: boolean;
  /** A number to show on the icon; hidden at zero. */
  count?: number;
}) {
  const labelColor = tabColor(active);

  return (
    <Link
      to={to}
      // Animate the tab-screen swap with the View Transitions API. Reuses the
      // app's `::view-transition-*(root)` fade + slide-up (see styles.css);
      // honours prefers-reduced-motion via the same rule.
      viewTransition
      className={cn(
        'relative flex h-full min-w-0 flex-col items-center justify-center gap-1 px-1'
      )}
      aria-current={active ? 'page' : undefined}
    >
      {active ? (
        <span
          aria-hidden
          className="bg-primary absolute inset-x-4 top-0 h-[3px] rounded-b-full"
        />
      ) : null}
      {count > 0 ? (
        <span
          role="status"
          aria-label={`${count} waiting for a reply`}
          className="bg-primary absolute top-1.5 left-1/2 ml-2 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-none font-bold text-primary-foreground tabular-nums"
        >
          {formatNavCount(count)}
        </span>
      ) : null}
      <Icon
        className="size-6 shrink-0"
        style={{ color: labelColor }}
        strokeWidth={active ? 2.5 : 2}
        aria-hidden
      />
      <span
        className={cn(
          'w-full truncate text-center text-[10px] leading-tight',
          active ? 'font-bold' : 'font-medium'
        )}
        style={{ color: labelColor }}
      >
        {label}
      </span>
      {adminOnly ? <AdminOnlyBadge className="absolute top-1 right-1" /> : null}
    </Link>
  );
}
